// CURRENTLY HKDL ONLY

import {
    Component,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { from, Subject } from 'rxjs';
import { take, takeUntil, switchMap, map } from 'rxjs/operators';

import { ResourceBundleService } from '@app/services/resource-bundle/resource-bundle.service';
import { ConfigService } from '@core/config.service';
import { SanitizerService } from '@app/services/sanitizer/sanitizer.service';
import { CTA } from '@app/features/homepage/components/non-fast-hero/interfaces/cta.interface';
import { WindowSizeService } from '@app/shared/services/window-size/window-size.service';
import { WindowSizeEnum } from '@app/shared/services/window-size/window-size.enum';
import { LoggerService } from '@wdpr/ra-angular-logger';
import { Brands } from '@app/shared/utils/utils.enum';
import { getDefaultImageUrl } from '@app/shared/utils/media-engine';

@Component({
    selector: 'app-non-fast-hero',
    templateUrl: 'non-fast-hero.component.html',
    styleUrls: ['non-fast-hero.component.scss'],
})
export class NonFastHeroComponent implements OnInit, OnDestroy {
    @HostBinding('class.fullscreen') isFullscreen = false;
    @Input() ME?: any;

    public titleMobile?: string;
    public titleDesktop?: string;
    public headerMobile?: string;
    public headerDesktop?: string;
    public videoFirstCTA: CTA;
    public mobileVideoFirstCTA: CTA;
    public videoDetailsCTA: CTA;
    public mobileVideoDetailsCTA: CTA;
    public hasFirstCTA: boolean;
    public isMobile: boolean;
    public siteId: string;
    public defaultImageUrl: string;
    public heroData: any;
    private destroy$ = new Subject<void>();
    private sameDevice: WindowSizeEnum;

    constructor(
        private resourceBundleService: ResourceBundleService,
        private configService: ConfigService,
        private sanitizerService: SanitizerService,
        private windowSizeService: WindowSizeService,
        private logger: LoggerService
    ) {}

    ngOnInit() {
        this.siteId = this.configService.getValue('siteId');
        this.isMobile = this.windowSizeService.getDevice() === WindowSizeEnum.Mobile;
        this.heroData = this.resourceBundleService.get('heroes');

        from(this.heroData)
            .pipe(take(1))
            .subscribe({
                next: (response: any) => {
                    const descriptions = response?.heroes[0]?.descriptions;
                    this.setUpHero(descriptions);
                },
                error: (error) => {
                    this.logger.error('Error getting heroes resource bundle', error);
                },
            });

        this.windowSizeService
            .onResize()
            .pipe(
                switchMap((device) =>
                    from(this.heroData).pipe(
                        map((data) => ({ device, data }))
                    )
                ),
                takeUntil(this.destroy$)
            ).subscribe({
                next: ({ device, data }) => {
                    this.isMobile = device === WindowSizeEnum.Mobile;

                    // prevent unnecessary calls between devices after the first resize
                    if (device === this.sameDevice) {
                        return;
                    }
                    // set for next resize
                    this.sameDevice = device;

                    this.defaultImageUrl = getDefaultImageUrl(data, this.isMobile);
                    const mediaImage = document.querySelectorAll('.me-media-image');
                    if (mediaImage && mediaImage.length > 0) {
                        mediaImage.forEach((image) => {
                            image.setAttribute('src', this.defaultImageUrl);
                        });
                    }
                },
                error: (error) => {
                    this.logger.error('Error updating hero on resize', error);
                }
            });
    }

    setUpHero(descriptions) {
        const analyticsSiteId = this.siteId.toUpperCase();
        const firstCtaDesktop = descriptions?.videoPlayCTA?.sections;
        const firstCtaMobile = descriptions?.mobileVideoPlayCTA?.sections;
        const detailsCtaDesktop =
            descriptions?.videoDetailsCTA?.sections?.length > 0
                ? descriptions?.videoDetailsCTA?.sections
                : descriptions?.videoDetailsCTA;
        const detailsCtaMobile = descriptions?.mobileVideoDetailsCTA;

        this.setHeadersAndTitles(descriptions);

        if (firstCtaDesktop && firstCtaMobile) {
            this.hasFirstCTA = true;
            this.mobileVideoFirstCTA = this.setCTA(firstCtaMobile, false, analyticsSiteId);
            this.videoFirstCTA = this.setCTA(firstCtaDesktop, false, analyticsSiteId);
        }

        this.mobileVideoDetailsCTA = this.setCTA(detailsCtaMobile, true, analyticsSiteId);
        this.videoDetailsCTA = this.setCTA(detailsCtaDesktop, true, analyticsSiteId);
    }

    setHeadersAndTitles(descriptions) {
        this.titleMobile = this.sanitizerService.decodeHTML(
            descriptions?.mobileVideoTitle?.sections?.title
        );
        this.titleDesktop = this.sanitizerService.decodeHTML(
            descriptions?.videoTitle?.sections?.title
        );
    }

    setCTA(ctaInfo, isDetails: boolean, analyticsSiteId: string): CTA {
        let target = ctaInfo?.link?.trim() ? ctaInfo.link : '_self';
        const href = ctaInfo?.ctaUrl;
        let click;

        if (target === '_self' && !href) {
            target = undefined;
            click = this.enterFullscreen;
        }

        let cta: CTA = {
            alt: this.sanitizerService.decodeHTML(ctaInfo?.alt),
            analyticsLinkId: ctaInfo.analyticsLinkId,
            title: this.sanitizerService.decodeHTML(ctaInfo?.title),
            target,
            icon: ctaInfo?.icon,
            ctaURL: ctaInfo?.ctaURL,
            click,
        };

        if (isDetails) {
            cta.analyticsLinkId = `${analyticsSiteId}_HPMP_Billboard_${cta.analyticsLinkId}_Link`;
            cta.analyticsLinkIdFullscreen = `${analyticsSiteId}_HPMP_InVideo_${cta.analyticsLinkId}_Link`;
        }

        cta.alt = this.sanitizerService.decodeHTML(ctaInfo?.title);
        cta.target = ctaInfo?.target;
        cta.ctaURL = ctaInfo?.link;

        return cta;
    }

    enterFullscreen(event: Event) {
        event.preventDefault();

        const meInstance = (window as any).appComponentmediaEngineInstance1;
        if (!meInstance) return;

        try {
            this.isFullscreen = true;

            if (meInstance.config?.callbacks) {
                meInstance.config.callbacks.customFullscreenExit = () => {
                    document.body.classList.remove('pixie-fullscreen');
                    this.isFullscreen = false;
                    document.getElementById('media-engine-hero')?.classList.remove('me-fullscreen');
                    meInstance.changeMediaSet(1);
                    meInstance.fullscreenExit();
                    document.getElementById('headerWrapper').style.display = 'block';
                };
            }

            document.getElementById('headerWrapper').style.display = 'none';
            if (meInstance.changeMediaSet) meInstance.changeMediaSet(2);
            if (meInstance.fullscreenEnter) meInstance.fullscreenEnter();
        } catch (error) {
            console.error('Fullscreen error:', error);
        }
    }

    ngOnDestroy(): void {
        this.isFullscreen = false;
        this.destroy$.next();
        this.destroy$.complete();
    }
}

declare global {
    interface Window {
        appComponentmediaEngineInstance1: any;
        MEConfig: any;
        ME: any;
    }
}
